<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.entities.manufacturer', 2), to: '/settings/manufacturers' }
    ]"
  >
    <template #right>
      <Button
        label="Add Manufacturer"
        severity="primary"
        icon="plus"
        @click="newManufacturer"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Manufacturers"
      :value="manufacturers"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-manufacturers"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.id }}</div>
        </template>
      </Column>

      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.name }}</div>
        </template>
      </Column>

      <Column
        field="squareLogoUrl"
        header="Square logo"
      >
        <template #body="{ data }">
          <Image
            :src="data.squareLogoUrl"
            image-class="max-w-8"
          />
        </template>
      </Column>

      <Column
        field="logoUrl"
        header="Logo"
      >
        <template #body="{ data }">
          <Image
            :src="data.logoUrl"
            image-class="h-8 max-w-16"
          />
        </template>
      </Column>

      <Column
        field="accentColor"
        header="Brand Colour"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <Chip
              v-if="data.accentColor"
              :color="data.accentColor"
            >
              {{ _toUpper(data.accentColor) }}
            </Chip>
          </div>
        </template>
      </Column>

      <Column
        field="branding"
        header="Brand Profile"
      >
        <template #body="{ data }">
          <div>{{ data.branding?.name }}</div>
        </template>
      </Column>

      <Column
        field="regions"
        header="Regions"
      >
        <template #body="{ data }">
          <div class="flex gap-2">
            <Chip
              v-for="(region, index) in data.regions"
              :key="index"
            >
              <img
                :src="regionsList.find(r => r.value == region.toLowerCase()).image"
                class="w-5 mr-2"
              />
              {{ region.toUpperCase() }}
            </Chip>
          </div>
        </template>
      </Column>

      <Column
        field="isActive"
        header="Active"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isActive"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Active
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="danger"
          >
            Disabled
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="[
              { value: 1, label: 'Active' },
              { value: 0, label: 'Disabled' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column :sortable="false">
        <template #body="{ data }">
          <div>
            <Button
              v-tippy="$t('ui.actions.edit')"
              icon="cog"
              plain
              severity="secondary"
              @click="editManufacturer(data)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="editModalIsOpen"
      title="Edit Manufacturer"
      icon="industry"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="editHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Save"
      :show-loader-on-confirm="true"
      :is-confirming="editApiRequest.isLoading.value"
      :disable-close="editApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="((editModalIsOpen = false), (editHasUnsavedChanges = false))"
      @ok-modal="submitEditManufacturer()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          label="Active"
          class="col-span-2"
        >
          <ToggleSwitch v-model="editState.isActive" />
        </FormGroup>
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="editState.name"
            :has-error="v$edit.name.$error"
          />
          <InputError :has-error="v$edit.name.$error">{{ v$edit.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.locationSelection.region', 2)"
          :is-required="true"
        >
          <Select
            v-model="editState.regions"
            :options="regionsList"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$edit.regions.$error">{{ v$edit.regions.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>

      <!-- Branding section only applies when the manufacturer has a linked brand profile -->
      <div
        v-if="editState.brandProfileId"
        class="mt-4"
      >
        <div class="font-medium mb-2">Brand Profile</div>
        <div class="grid grid-cols-2 gap-x-6">
          <FormGroup
            :label="$t('ui.common.name')"
            :is-required="true"
          >
            <Input
              v-model="editState.branding.name"
              :has-error="v$edit.branding.name.$error"
            />
            <InputError :has-error="v$edit.branding.name.$error">{{ v$edit.branding.name.$errors[0]?.$message }}</InputError>
          </FormGroup>
          <FormGroup label="Accent Colour">
            <div class="flex items-center gap-2">
              <Input
                v-model="editState.branding.accentColor"
                placeholder="#000000"
              />
              <span
                v-if="editState.branding.accentColor"
                class="inline-block h-6 w-6 shrink-0 rounded-full border border-[var(--border-color)]"
                :style="{ backgroundColor: editState.branding.accentColor }"
              ></span>
            </div>
          </FormGroup>
          <FormGroup label="UI Gradient 1">
            <Input
              v-model="editState.branding.uiGradient1"
              placeholder="#000000"
            />
          </FormGroup>
          <FormGroup label="UI Gradient 2">
            <Input
              v-model="editState.branding.uiGradient2"
              placeholder="#000000"
            />
          </FormGroup>
        </div>

        <div class="mt-2">
          <div class="font-medium mb-2">Logos</div>
          <div class="grid grid-cols-2 gap-x-6">
            <div class="border border-[var(--border-color)] rounded-md p-3">
              <div class="text-sm text-secondary mb-2">Logo (SVG)</div>
              <div class="h-12 flex items-center mb-2">
                <Image
                  v-if="editState.branding.logoUrl"
                  :src="editState.branding.logoUrl"
                  image-class="h-10 max-w-24"
                />
                <span
                  v-else
                  class="text-sm text-secondary"
                >
                  No logo uploaded
                </span>
              </div>
              <Button
                label="Upload SVG"
                severity="secondary"
                outlined
                size="sm"
                icon="cloud-arrow-up"
                :is-loading="logoIsUploading"
                @click="triggerLogoUpload('svg')"
              />
            </div>
            <div class="border border-[var(--border-color)] rounded-md p-3">
              <div class="text-sm text-secondary mb-2">Logo (PNG - used in emails)</div>
              <div class="h-12 flex items-center mb-2">
                <Image
                  v-if="editState.branding.logoPngUrl"
                  :src="editState.branding.logoPngUrl"
                  image-class="h-10 max-w-24"
                />
                <span
                  v-else
                  class="text-sm text-secondary"
                >
                  No logo uploaded
                </span>
              </div>
              <Button
                label="Upload PNG"
                severity="secondary"
                outlined
                size="sm"
                icon="cloud-arrow-up"
                :is-loading="logoIsUploading"
                @click="triggerLogoUpload('png')"
              />
            </div>
          </div>
          <input
            ref="logoFileInput"
            type="file"
            :accept="logoUploadType === 'svg' ? '.svg' : '.png'"
            style="display: none"
            @change="uploadLogo"
          />
        </div>
      </div>
    </Modal>

    <Modal
      :is-open="newModalIsOpen"
      title="Add Manufacturer"
      icon="industry"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="newHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Submit"
      :show-loader-on-confirm="true"
      :is-confirming="newApiRequest.isLoading.value"
      :disable-close="newApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="((newModalIsOpen = false), (newHasUnsavedChanges = false))"
      @ok-modal="submitNewManufacturer()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="newState.name"
            :has-error="v$new.name.$error"
          />
          <InputError :has-error="v$new.name.$error">{{ v$new.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.locationSelection.region')"
          :is-required="true"
        >
          <Select
            v-model="newState.regions"
            :options="regionsList"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$new.regions.$error">{{ v$new.regions.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
      <div class="text-sm text-secondary">A default brand profile will be created for the manufacturer automatically.</div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref, watch } from 'vue'

// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Modal from '@/components/modal/Modal.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'
import Select from '@/components/forms/Select.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// utilities
import { alertToast } from '@/utilities/notification'

import { FilterMatchMode } from '@primevue/core/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import _toUpper from 'lodash/toUpper'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

const manufacturers = ref(null)
const manufacturersApiRequest = useApiRequest()
const isLoading = manufacturersApiRequest.isLoading

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    isActive: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-settings-manufacturers')
  initFilters()
}

const regionsList = [
  {
    label: useI18n().t('meta.countries.gb.label'),
    value: 'gb',
    emoji: '🇬🇧',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/GB.svg'
  },
  {
    label: useI18n().t('meta.countries.us.label'),
    value: 'us',
    emoji: '🇺🇸',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg'
  },
  {
    label: useI18n().t('meta.countries.es.label'),
    value: 'es',
    emoji: '🇪🇸',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/ES.svg'
  },
  {
    label: useI18n().t('meta.countries.mx.label'),
    value: 'mx',
    emoji: '🇲🇽',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/MX.svg'
  },
  {
    label: useI18n().t('meta.countries.fr.label'),
    value: 'fr',
    emoji: '🇫🇷',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/FR.svg'
  }
]

/**
 * Fetch all manufacturers (including disabled ones so they remain editable)
 */
function fetchManufacturers() {
  manufacturersApiRequest
    .send({
      endpoint: '/v1/manufacturers',
      params: { showDisabled: true }
    })
    .then(response => {
      if (response.success) {
        manufacturers.value = response.data
      } else {
        alertToast('Error', 'Failed to fetch manufacturers', 'error')
      }
    })
}
fetchManufacturers()

const editApiRequest = useApiRequest()
const newApiRequest = useApiRequest()
const logoUploadApiRequest = useApiRequest()
const logoIsUploading = logoUploadApiRequest.isLoading

const editModalIsOpen = ref(false)
const editHasUnsavedChanges = ref(false)
const editInitState = ref(null)
const editState = ref({ branding: {} })

const newModalIsOpen = ref(false)
const newHasUnsavedChanges = ref(false)
const newInitState = ref(null)
const newState = ref({})

const logoFileInput = ref(null)
const logoUploadType = ref('svg')

const editRules = {
  name: { required },
  regions: { required },
  branding: {
    // Brand profile name is only required when a profile is linked
    name: { required: requiredIf(() => !!editState.value.brandProfileId) }
  }
}
const v$edit = useVuelidate(editRules, editState, { $stopPropagation: true })

const newRules = {
  name: { required },
  regions: { required }
}
const v$new = useVuelidate(newRules, newState)

/**
 * Open the merged edit modal for a manufacturer (details + brand profile)
 * @param {object} manufacturer - Row data from the manufacturers table
 */
function editManufacturer(manufacturer) {
  editInitState.value = {
    id: manufacturer.id,
    name: manufacturer.name,
    regions: manufacturer.regions.map(region => region.toLowerCase()),
    isActive: manufacturer.isActive,
    brandProfileId: manufacturer.brandProfileId,
    branding: {
      name: manufacturer.branding?.name,
      logoUrl: manufacturer.branding?.logoUrl,
      logoPngUrl: manufacturer.branding?.logoPngUrl,
      accentColor: manufacturer.branding?.accentColor,
      uiGradient1: manufacturer.branding?.uiGradient1,
      uiGradient2: manufacturer.branding?.uiGradient2
    }
  }
  editState.value = { ...editInitState.value, branding: { ...editInitState.value.branding } }
  editModalIsOpen.value = true
}

/**
 * Open the add manufacturer modal with a blank state
 */
function newManufacturer() {
  newInitState.value = {
    name: null,
    regions: null
  }
  newState.value = { ...newInitState.value }
  newModalIsOpen.value = true
}

/**
 * Save manufacturer details and brand profile in one PATCH request
 */
async function submitEditManufacturer() {
  const isFormCorrect = await v$edit.value.$validate()
  // Block save while validation errors are present
  if (!isFormCorrect) {
    return
  }

  const dataToPost = {
    name: editState.value.name,
    regions: editState.value.regions,
    isActive: editState.value.isActive
  }
  // Branding updates only apply when the manufacturer has a linked brand profile
  if (editState.value.brandProfileId) {
    dataToPost.branding = {
      name: editState.value.branding.name,
      accentColor: editState.value.branding.accentColor,
      uiGradient1: editState.value.branding.uiGradient1,
      uiGradient2: editState.value.branding.uiGradient2
    }
  }

  try {
    const response = await editApiRequest.send({
      method: 'PATCH',
      endpoint: `/v1/manufacturers/${editState.value.id}`,
      data: JSON.stringify(dataToPost)
    })
    if (!response.success) {
      alertToast('Error', 'Failed to update manufacturer', 'error')
      return
    }
    alertToast('Success!', 'Manufacturer updated', 'success')
    editModalIsOpen.value = false
    editHasUnsavedChanges.value = false
    fetchManufacturers()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$edit.value.$reset()
}

/**
 * Create a new manufacturer (a default brand profile is created by the API)
 */
async function submitNewManufacturer() {
  const isFormCorrect = await v$new.value.$validate()
  // Block submit while validation errors are present
  if (!isFormCorrect) {
    return
  }

  const dataToPost = { ...newState.value }
  try {
    const response = await newApiRequest.send({
      method: 'POST',
      endpoint: '/v1/manufacturers',
      data: JSON.stringify(dataToPost)
    })
    if (!response.success) {
      alertToast('Error', 'Failed to add manufacturer', 'error')
      return
    }
    alertToast('Success!', 'Manufacturer added', 'success')
    newModalIsOpen.value = false
    newHasUnsavedChanges.value = false
    fetchManufacturers()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$new.value.$reset()
}

/**
 * Open the hidden file input for the requested logo type
 * @param {'svg'|'png'} type - Logo variant to upload
 */
function triggerLogoUpload(type) {
  logoUploadType.value = type
  logoFileInput.value.click()
}

/**
 * Upload a logo file to the manufacturer's linked brand profile
 * @param {Event} event - File input change event
 */
async function uploadLogo(event) {
  const file = event.target.files[0]
  event.target.value = ''
  // Nothing selected in the file dialog
  if (!file) {
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await logoUploadApiRequest.send({
      method: 'POST',
      endpoint: `/v1/brand-profiles/${editState.value.brandProfileId}/logo`,
      data: formData
    })
    if (!response.success) {
      alertToast('Error', 'Failed to upload logo', 'error')
      return
    }
    alertToast('Success!', 'Logo uploaded', 'success')
    // API returns logoUrl (svg) or logoPngUrl (png) for the uploaded file;
    // update the init state too since the upload is already persisted
    if (response.data.logoUrl) {
      editState.value.branding.logoUrl = response.data.logoUrl
      editInitState.value.branding.logoUrl = response.data.logoUrl
    }
    if (response.data.logoPngUrl) {
      editState.value.branding.logoPngUrl = response.data.logoPngUrl
      editInitState.value.branding.logoPngUrl = response.data.logoPngUrl
    }
    fetchManufacturers()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}

watch(
  editState,
  newValue => {
    // Compare the current state with the initial state
    editHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(editInitState.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)

watch(
  newState,
  newValue => {
    // Compare the current state with the initial state
    newHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(newInitState.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)
</script>

<style></style>
